import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import Colors from '../../constants/Colors';
import {
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/core';
import { MyDataParamList } from '../../navigation/MyDataNav';
import { gActiveStudyParticipant } from '../../hooks/study/useHasActiveStudy';
import { useUserContext } from '../../context/UserContext';
import { Feather } from '@expo/vector-icons';
import { StackNavigationProp } from '@react-navigation/stack';
import EditMeasurements from '../../components/EditMeasurements';
import IconButton from '../../components/IconButton';
import Spacer from '../../components/Spacer';
import { UserAttributeSource } from '../../models/UserAttribute';
import { WearableDataLineChartCard } from '../study/screen-components/plugins/WearableDataLineChartCardPlugin';
import { useUserAttributesContext } from '../../context/UserAttributesContext';
import { View } from 'react-native';

const StyledScrollView = styled.ScrollView`
  flex: 1;
  width: 100%;
  padding: 0 16px;
`;

const StyledContainer = styled.View`
  flex: 1;
  background-color: ${Colors.background};
  padding: 0 0 16px 0;
`;

const StyledBackIconButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  margin-left: 24px;
`;

const MyDataMeasurementDetailsScreen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { latestAttributes, connectedWearableSources, syncData } =
    useUserAttributesContext();
  const { user, allOccurrences, studyParticipants } = useUserContext();
  const route =
    useRoute<RouteProp<MyDataParamList, 'MyDataMeasurementDetailsScreen'>>();
  const { activeStudyParticipant } = useMemo(() => {
    const activeStudyParticipant = gActiveStudyParticipant(
      studyParticipants || [],
      allOccurrences
    );
    return { activeStudyParticipant };
  }, [allOccurrences, studyParticipants]);

  useFocusEffect(
    // fix for data not converting to the correct unit system upon navigating
    //  back from the profile settings
    useCallback(() => {
      if (user?.id && connectedWearableSources?.length)
        syncData(user.id, connectedWearableSources);
    }, [syncData])
  );

  const wearableDataSources = useMemo(
    () => ({
      healthKit: { dataSource: route.params.type },
      googleFit: { dataSource: route.params.type },
      healthConnect: { dataSource: route.params.type },
      fitbit: { dataSource: route.params.type },
      garmin: { dataSource: route.params.type },
      oura: { dataSource: route.params.type }
    }),
    [route.params.type]
  );
  const [selectedSource, setSelectedSource] = useState<{
    source: UserAttributeSource;
    subSource?: string;
    sourceName?: string;
  }>();
  const embedded = useMemo(
    () => route.params?.isEmbedded,
    [route.params.isEmbedded]
  );
  return (
    <StyledContainer>
      {embedded && (
        <StyledBackIconButton
          onPress={() => {
            navigation.goBack();
          }}
        >
          <Feather name='chevron-left' size={24} />
        </StyledBackIconButton>
      )}
      <StyledScrollView overScrollMode='never' bounces={false}>
        <View style={{ flexGrow: 1 }}>
          {connectedWearableSources && latestAttributes && (
            <WearableDataLineChartCard
              connectedWearableSources={connectedWearableSources}
              latestAttributes={latestAttributes}
              variant='measurement'
              studyParticipant={activeStudyParticipant}
              wearableDataSources={wearableDataSources}
              landscapeDayCount={28}
              portraitDayCount={7}
              onSelectSource={(source, subSource, sourceName) => {
                setSelectedSource({ source, subSource, sourceName });
              }}
            />
          )}
          <Spacer size={5} />
          {selectedSource && (
            <EditMeasurements
              unitSystem={user?.unitSystem}
              disableEditing={embedded}
              type={route.params.type}
              config={selectedSource}
              onSaved={() => {
                if (user?.id && connectedWearableSources)
                  syncData(user.id, connectedWearableSources);
              }}
            />
          )}
        </View>
      </StyledScrollView>
    </StyledContainer>
  );
};

export default MyDataMeasurementDetailsScreen;
